import React from 'react';
import { Task } from '../types/todo';
import { Plane, Building, CheckCircle2, Clock, Circle, ChevronDown, Search, ListChecks, Luggage } from 'lucide-react';

interface TaskNavigationProps {
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  onUpdateStatus?: (taskId: string, status: string) => void;
}

export function TaskNavigation({ tasks, onTaskClick, onUpdateStatus }: TaskNavigationProps) {
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set(['before-departure', 'packing', 'after-arrival', 'return']));
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('all');

  const tasksByCategory = React.useMemo(() => {
    const filteredTasks = tasks.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = selectedStatus === 'all' || task.status === selectedStatus;
      return matchesSearch && matchesStatus;
    });

    const grouped = filteredTasks.reduce((acc, task) => {
      const category = task.category || 'uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(task);
      return acc;
    }, {} as Record<string, Task[]>);

    const orderedCategories = ['before-departure', 'packing', 'after-arrival', 'return'];
    return orderedCategories.reduce((acc, category) => {
      if (grouped[category]) {
        acc[category] = grouped[category];
      }
      return acc;
    }, {} as Record<string, Task[]>);
  }, [tasks, searchQuery, selectedStatus]);

  const hasFilteredTasks = Object.keys(tasksByCategory).length > 0;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'before-departure':
        return <ListChecks className="w-4 h-4 text-blue-500" />;
      case 'packing':
        return <Luggage className="w-4 h-4 text-amber-500" />;
      case 'after-arrival':
        return <Plane className="w-4 h-4 text-emerald-500" />;
      case 'return':
        return <Building className="w-4 h-4 text-teal-500" />;
      default:
        return null;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'before-departure':
        return 'Avant le départ';
      case 'packing':
        return 'Valise';
      case 'after-arrival':
        return 'Jour J';
      case 'return':
        return 'Arrivée';
      default:
        return category;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
      case 'in-progress':
        return <Clock className="w-3.5 h-3.5 text-amber-500" />;
      default:
        return <Circle className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'not-started':
        return 'in-progress';
      case 'in-progress':
        return 'completed';
      case 'completed':
        return 'not-started';
      default:
        return 'not-started';
    }
  };

  const handleStatusClick = (e: React.MouseEvent, taskId: string, currentStatus: string) => {
    e.stopPropagation();
    if (onUpdateStatus) {
      onUpdateStatus(taskId, getNextStatus(currentStatus));
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const statusFilters = [
    { id: 'all', label: 'Tout', icon: null, color: 'text-gray-600 dark:text-gray-400' },
    { id: 'not-started', label: 'A faire', icon: <Circle className="w-3 h-3" />, color: 'text-gray-500' },
    { id: 'in-progress', label: 'En cours', icon: <Clock className="w-3 h-3" />, color: 'text-amber-500' },
    { id: 'completed', label: 'Fini', icon: <CheckCircle2 className="w-3 h-3" />, color: 'text-emerald-500' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 shadow-sm flex flex-col h-full overflow-hidden">
      <div className="p-3 space-y-2.5 border-b border-gray-100 dark:border-gray-700/50">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-gray-400 focus:border-gray-400 dark:bg-gray-700 dark:text-white bg-gray-50"
          />
        </div>

        <div className="flex gap-1">
          {statusFilters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setSelectedStatus(filter.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
                selectedStatus === filter.id
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {filter.icon}
              <span>{filter.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-0.5 custom-scrollbar">
        {hasFilteredTasks ? (
          Object.entries(tasksByCategory).map(([category, categoryTasks]) => (
            <div key={category}>
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                {getCategoryIcon(category)}
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex-1 text-left truncate">
                  {getCategoryLabel(category)}
                </span>
                <span className="text-[10px] text-gray-400 tabular-nums">
                  {categoryTasks.filter(t => t.status === 'completed').length}/{categoryTasks.length}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                  expandedCategories.has(category) ? 'rotate-180' : ''
                }`} />
              </button>

              {expandedCategories.has(category) && (
                <div className="ml-2 space-y-px">
                  {categoryTasks.map(task => (
                    <div key={task.id} className="flex items-center gap-1.5 group">
                      <button
                        onClick={(e) => onUpdateStatus && handleStatusClick(e, task.id, task.status)}
                        className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        {getStatusIcon(task.status)}
                      </button>
                      <button
                        onClick={() => onTaskClick(task.id)}
                        className="flex-1 py-1.5 px-1 text-left rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors min-w-0"
                      >
                        <span className={`text-xs text-gray-600 dark:text-gray-400 line-clamp-1 ${
                          task.status === 'completed' ? 'line-through text-gray-400 dark:text-gray-500' : ''
                        }`}>
                          {task.title}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-center p-4">
            <Search className="w-5 h-5 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {searchQuery ? 'Aucun resultat' : 'Aucune tache'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
